import React from "react";
import {
  AuthContextProvider,
  useAuth,
  IAuthContext,
} from "contexts/AuthContext";
import { Toaster } from "components/ui/sonner";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { ErrorBoundary } from "@sentry/react";
import { ErrorFallback } from "components/errors/ErrorFallbackScreen";
import { RouteErrorScreen } from "components/errors/RouteErrorScreen";
import { NotFound } from "pages/NotFound";
import { logError } from "shared/services/ErrorReporting";

// Import the generated route tree
import { routeTree } from "./routeTree.gen";
import { FeatureFlagProvider } from "./contexts/FeatureFlagContext";
import { LayoutProvider } from "./contexts/LayoutContext";

// Create a new router instance
const router = createRouter({
  routeTree,
  context: {
    auth: undefined! as IAuthContext,
  },
  // An address the app does not have lands on the app's own page rather than
  // the router's built-in default. The page and its copy were already written
  // and mounted nowhere, so nothing could ever render them; NAV-86 is what made
  // that visible, by deleting `/group-legal/approvals` outright — a page with
  // no data source behind it, so there is nothing to forward a stale bookmark
  // *to*. A stub redirecting it somewhere else would tell the reader their
  // bookmark still works, which is the opposite of true.
  defaultNotFoundComponent: NotFound,
  // The app's own page for a route that throws, for the same reason (Jira
  // NAV-169): the router catches before `ErrorBoundary` below can, and its
  // built-in page prints the raw error and reports nothing.
  defaultErrorComponent: RouteErrorScreen,
});

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }

  interface RouterContext {
    auth: IAuthContext;
  }
}

const InnerApp = () => {
  const auth = useAuth();

  // Mount the router only once the initial auth state (claims + first
  // profile snapshot) is resolved. Route guards run on the very first
  // navigation; mounting earlier makes them see role=null for a logged-in
  // user and redirect away, losing deep links on every full page load.
  if (auth.loading) {
    return <div className="bg-background min-h-screen" />;
  }

  return <RouterProvider router={router} context={{ auth }} />;
};

const App: React.FC = () => (
  // `onError` is what makes a render crash reportable. The boundary's own
  // reporting is `Sentry.captureException`, which is a no-op on the empty DSN
  // every environment has — so without this the one error a user definitely
  // notices, the blank page, would be the one error we never hear about
  // (PRD J5, ADR 0019). The `window` listeners in `installClientErrorSink`
  // cover what never reaches a boundary at all.
  <ErrorBoundary fallback={ErrorFallback} onError={(error) => logError(error)}>
    <AuthContextProvider>
      <FeatureFlagProvider>
        <LayoutProvider>
          <InnerApp />
        </LayoutProvider>
        <Toaster />
      </FeatureFlagProvider>
    </AuthContextProvider>
  </ErrorBoundary>
);

export default App;
